import React, { useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useLocale } from '../context/LocaleContext';
import { useContent } from '../hooks/useContent';
import { useNavigationAwareAnimations } from '../hooks/useNavigationAwareAnimations';
import { preloadCriticalImages, preloadImagesForSolutionsPath } from '../utils/imagePreloader';
import { PRELOAD_HOME_CRITICAL } from '../config/preloadConfig';
import PreloadZone from '../components/PreloadZone';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import {
  Container,
  SectionTitle,
  HeroSection,
  HeroContent,
  HeroTitle,
  HeroSubtitle,
  CTAButton,
  LazyBackgroundImage,
  CtaImageSection,
  ImageContainer,
  ImageOverlay,
  ImageDescription,
  ContentText,
  BulletCardsGrid,
  BulletCard,
  BulletCardIcon,
  BulletCardText,
  CheckIcon,
  PartnershipSection,
  PartnershipContent,
  SolutionsCtaTitle,
  SolutionsCtaText,
  SolutionsCtaButton,
  staggerContainer,
  staggerItem,
  CardArrowIcon,
  CardArrowButton,
  StrengthCardTitle,
  StrengthCardDescription,
  ItemCardLight,
  ItemGrid,
  ItemTitle,
  ExecutionCapabilityCard,
  ExecutionCapabilityNumber,
  SectionDivider,
} from '../components/shared/SharedComponents';
import GlobalStats from '../components/GlobalStats';
import { SHOW_NEW_PAGES_IN_NAV, HIDDEN_NAV_PATHS } from '../config/features';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;


// Main Container
const HomeContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(250, 252, 255, 0.95) 20%,
    rgba(245, 249, 255, 0.9) 40%,
    rgba(240, 245, 252, 0.85) 60%,
    rgba(236, 242, 249, 0.9) 80%,
    rgba(248, 250, 252, 0.95) 100%
  );
  animation: ${fadeIn} 0.6s ease-out;
`;


// Content Sections
const Section = styled.section`
  padding: 20px 0 40px;
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: translateY(${props => props.isVisible ? '0' : '30px'});
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0.05s;
`;

// Grid layouts
const Grid = styled.div`
  display: grid;
  gap: 32px;
  width: 100%;
  
  &.four-col {
    grid-template-columns: repeat(4, 1fr);
    
    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 24px;
    }
  }
  
  &.two-col {
    grid-template-columns: repeat(2, 1fr);

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  /* Sector Expertise: equal-height cards */
  &.solutions-grid.two-col {
    align-items: stretch;

    > * {
      display: block;
      min-height: 100%;
    }
  }
  
  &.single-col {
    grid-template-columns: 1fr;
    gap: 60px;
    max-width: 1000px;
  }
`;

const SolutionCardLink = styled(Link)`
  position: relative;
  border-radius: 24px;
  background-color: #ffffff;
  border: 1px solid rgba(226, 232, 240, 0.6);
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  display: grid;
  grid-template-columns: ${({ $hasImage }) => ($hasImage ? 'minmax(0, 1.1fr) minmax(0, 1.4fr)' : '1fr')};
  min-height: 220px;
  height: 100%;
  align-items: stretch;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: translateY(${({ isVisible }) => (isVisible ? '0' : '30px')});
  transition: all 0.3s ease;
  transition-delay: ${({ delay }) => delay || '0.05s'};
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  & * {
    cursor: inherit;
  }

  &:hover {
    box-shadow: 0 20px 40px rgba(15, 23, 42, 0.12);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const IndustryImage = styled.div`
  display: ${({ $hasImage }) => ($hasImage ? 'block' : 'none')};
  background: ${({ $image }) => $image ? `url(${$image}) center/cover` : 'none'};
  min-height: 0;
  height: 100%;
  align-self: stretch;
  cursor: pointer;

  @media (max-width: 768px) {
    min-height: 140px;
    aspect-ratio: 16/10;
    height: auto;
    align-self: auto;
  }
`;

const IndustryContent = styled.div`
  padding: clamp(22px, 4vw, 32px);
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  cursor: pointer;
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1d334e;
  margin-bottom: ${props => props.mb || '16px'};
  line-height: 1.3;
  cursor: pointer;
`;

const Description = styled.p`
  font-size: ${props => props.size || '15px'};
  color: rgba(29, 51, 78, 0.8);
  line-height: 1.6;
  margin: 0;
  cursor: pointer;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ToggleButtonContainer = styled.div`
  margin-top: 32px;
  text-align: center;
`;

const ToggleButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  font-size: clamp(14px, 1.8vw, 16px);
  font-weight: 600;
  border-radius: 999px;
  background: linear-gradient(135deg, #1d334e, #BF1922);
  color: #ffffff;
  border: none;
  cursor: pointer;
  text-decoration: none;
  letter-spacing: 0.4px;
  transition: all 0.3s ease;
  box-shadow: 0 12px 28px rgba(29, 51, 78, 0.22);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 36px rgba(29, 51, 78, 0.28);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 10px 22px rgba(29, 51, 78, 0.2);
  }
`;

// BulletCard with vertical center alignment
const CenteredBulletCard = styled(BulletCard)`
  align-items: center;
`;

/** Scroll-reveal on shared ItemCard (section enter animation) */
const AnimatedItemCard = styled(ItemCardLight)`
  opacity: ${(p) => (p.$isVisible !== false ? 1 : 0)};
  transform: translateY(${(p) => (p.$isVisible !== false ? '0' : '30px')});
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  transition-delay: ${(p) => p.$delay || '0s'};
`;

/** Distinct from other pages: left-aligned copy; content sits in upper area, not vertically centered */
const HomeHeroSection = styled(HeroSection)`
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  padding: 140px 0 80px;

  @media (max-width: 768px) {
    padding: 110px 0 48px;
  }
`;

const HomeHeroContent = styled(HeroContent)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  text-align: left;

  @media (max-width: 768px) {
    align-items: flex-start;
    text-align: left;
  }
`;

const HeroSuperTitle = styled(motion.p)`
  font-size: clamp(14px, 1.8vw, 18px);
  font-weight: 600;
  letter-spacing: 2px;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 36px;
`;

const HomeHeroTitle = styled(HeroTitle)`
  margin-top: clamp(12px, 2vh, 20px);
  
  @media (max-width: 768px) {
    margin-top: clamp(12px, 2vh, 20px);
  }
`;

/** Left-aligned: no auto horizontal margins from shared HeroSubtitle */
const HomeHeroSubtitle = styled(HeroSubtitle)`
  width: 100%;
  margin-left: 0;
  margin-right: 0;
`;

const HomeCTAButton = styled(CTAButton)`
  width: fit-content;
  font-size: 18px;
  font-weight: 700;
`;

const SupportingSection = styled.section`
  padding: 60px 0;
  position: relative;
`;

const CardWrapper = styled.div`
  margin: 0 auto;
`;

const GlassmorphicCard = styled.div`
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(18px);
  border-radius: 18px;
  padding: 32px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 24px;
    border-radius: 16px;
  }
`;

const GlassmorphicCardDescription = styled(ImageDescription)`
  margin-bottom: 8px;
  color: #1d334e;
  font-size: clamp(16px, 2vw, 20px);
  font-weight: 700;
`;

const InstitutionalSection = styled.section`
  padding: 20px 0 64px;
`;

const InstitutionalBody = styled(ContentText)`
  margin-bottom: 24px;

  p {
    margin: 0 0 14px;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const WhoWeEngageColumnLabel = styled.p`
  font-size: clamp(18px, 2.2vw, 20px);
  font-weight: 600;
  color: #1d334e;
  margin: 0 0 16px 0;
`;

const WhoWeEngageGrid = styled(Grid)`
  margin-top: 24px;
  gap: 32px;
  align-items: start;
`;

const Home = () => {
  const navigate = useNavigate();
  const { getLocalePath } = useLocale();
  const { home, footerInfo, contactInfo, site, solutions } = useContent();
  const { observeElement, isVisible } = useNavigationAwareAnimations();

  // Preload critical images (hero placeholder + partnership CTA GIF; config is single source of truth)
  useEffect(() => {
    if (PRELOAD_HOME_CRITICAL.length > 0) {
      preloadCriticalImages(PRELOAD_HOME_CRITICAL);
    }
  }, []);

  useEffect(() => {
    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section, index) => {
      observeElement(section, `section-${index}`);
    });
  }, [observeElement]);

  const preloadSolutionsOnHover = useCallback(
    (path) => () => preloadImagesForSolutionsPath(path, solutions),
    [solutions]
  );

  // Sector Expertise cards from home.solutions (content.json about.solutions.items)
  const allSolutions = home?.solutions?.items?.map((item, index) => ({
    id: item.path?.replace(/^\/solutions\//, '') || `sector-${index}`,
    title: item.title,
    description: item.description,
    image: item.image,
    path: item.path || '#'
  })) ?? [];
  const solutionsList = SHOW_NEW_PAGES_IN_NAV
    ? allSolutions
    : allSolutions.filter((s) => !HIDDEN_NAV_PATHS.includes(s.path));

  const heroMetaDescription = [home.hero.subtitle, home.hero.subtitleExtra]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <SEO
        title={home.hero.title}
        description={heroMetaDescription}
        image={home.hero.backgroundImage}
      />
      <StructuredData
        type="WebPage"
        data={{
          page: {
            title: home.hero.title,
            description: heroMetaDescription,
            url: '/',
            image: home.hero.backgroundImage
          },
          organization: { footerInfo, contactInfo },
          site
        }}
        includeOrganization={true}
        includeWebSite={true}
      />
      <HomeContainer>
        {/* Hero Section */}
        <LazyBackgroundImage
          src={home.hero.backgroundImage}
          placeholderImage="/hero/placeholder.png"
          critical
          rounded={false}
          transitionDuration={0.85}
          aria-label={home.hero.title}
          borderRadius={0}
        >
          <HomeHeroSection>
            <Container>
              <HomeHeroContent
                as={motion.div}
                initial="hidden"
                animate="visible"
                style={{ animation: 'none' }}
                variants={{
                  hidden: {},
                  visible: {
                    transition: { staggerChildren: 0.12, delayChildren: 0.15 },
                  },
                }}
              >
                
                <HomeHeroTitle
                  as={motion.h1}
                  variants={{ hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } }}
                  transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                >
                  {home.hero.title}
                </HomeHeroTitle>
                {home.hero.supertitle && (
                  <HeroSuperTitle
                    variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}
                    transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                  >
                    {home.hero.supertitle}
                  </HeroSuperTitle>
                )}
                <HomeHeroSubtitle
                  as={motion.p}
                  variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                  transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                  style={home.hero.subtitleExtra ? { marginBottom: 12 } : undefined}
                >
                  {home.hero.subtitle}
                </HomeHeroSubtitle>
                {home.hero.subtitleExtra && (
                  <HomeHeroSubtitle
                    as={motion.p}
                    variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                    transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                  >
                    {home.hero.subtitleExtra}
                  </HomeHeroSubtitle>
                )}
                <HomeCTAButton
                  as={motion.button}
                  variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}
                  transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                  onClick={() => navigate(getLocalePath('/contact'))}
                >
                  {home.hero.cta}
                </HomeCTAButton>
              </HomeHeroContent>
            </Container>
          </HomeHeroSection>
        </LazyBackgroundImage>

        {/* Supporting Section */}
        <SupportingSection>
          <Container>
            <CardWrapper>
              <LazyBackgroundImage src="/hero/office-building.png">
                <ImageContainer>
                  <ImageOverlay>
                    <GlassmorphicCard>
                      {home.supportingSection?.description && (
                        <GlassmorphicCardDescription>
                          {home.supportingSection.description}
                        </GlassmorphicCardDescription>
                      )}
                      {home.supportingSection?.categories && home.supportingSection.categories.length > 0 && (
                        <ImageDescription style={{ color: 'rgba(29, 51, 78, 0.8)' }}>
                          {home.supportingSection.categories.join(' | ')}
                        </ImageDescription>
                      )}
                    </GlassmorphicCard>
                  </ImageOverlay>
                </ImageContainer>
              </LazyBackgroundImage>
            </CardWrapper>
          </Container>
        </SupportingSection>

        {/* Global Operating Footprint */}
        <GlobalStats data={home.globalStats} isVisible={isVisible('section-0')} />

        <SectionDivider />

        {/* Institutional Engagement Parameters */}
        <InstitutionalSection>
          <Container>
            <SectionTitle className="with-underline">{home.institutionalEngagementParameters.title}</SectionTitle>
              <InstitutionalBody>
                {home.institutionalEngagementParameters.paragraphs.map((paragraph, index) => (
                  <p key={`institutional-p-${index}`}>{paragraph}</p>
                ))}
              </InstitutionalBody>
              <BulletCardsGrid>
                {home.institutionalEngagementParameters.items.map((item, index) => (
                  <CenteredBulletCard key={`institutional-item-${index}`}>
                    <BulletCardIcon>
                      <CheckIcon />
                    </BulletCardIcon>
                    <BulletCardText>{item}</BulletCardText>
                  </CenteredBulletCard>
                ))}
              </BulletCardsGrid>
              <InstitutionalBody style={{ marginBottom: 0, marginTop: '24px' }}>
                <p>{home.institutionalEngagementParameters.conclusion}</p>
              </InstitutionalBody>
          </Container>
        </InstitutionalSection>

        <SectionDivider />

        {/* Platform Scope & Engagement Model */}
        {home.platformScope && (
          <InstitutionalSection>
            <Container>
              <SectionTitle className="with-underline">{home.platformScope.title}</SectionTitle>
              <InstitutionalBody>
                {home.platformScope.paragraphs.map((paragraph, index) => (
                  <p key={`platform-scope-p-${index}`}>{paragraph}</p>
                ))}
              </InstitutionalBody>
              <ItemGrid style={{ marginTop: 24, marginBottom: 0 }}>
                {home.platformScope.items.map((item, index) => (
                  <ItemCardLight key={`platform-scope-item-${index}`}>
                    <ItemTitle style={{ marginBottom: 10 }}>{item.title}</ItemTitle>
                    <StrengthCardDescription>{item.description}</StrengthCardDescription>
                  </ItemCardLight>
                ))}
              </ItemGrid>
              <InstitutionalBody style={{ marginBottom: 0, marginTop: '24px' }}>
                <p>{home.platformScope.conclusion}</p>
              </InstitutionalBody>
            </Container>
          </InstitutionalSection>
        )}

        <SectionDivider />

        {/* Execution Platform Capabilities */}
        <Section data-animate isVisible={isVisible('section-1')}>
          <Container>
            <SectionTitle className="with-underline">{home.coreStrengths.title}</SectionTitle>
            <ItemGrid style={{ marginTop: 32, marginBottom: 32 }}>
              {home.coreStrengths.items.map((strength, index) => (
                <ExecutionCapabilityCard
                  key={index}
                  $isVisible={isVisible('section-1')}
                  $delay={`${0.05 + index * 0.08}s`}
                >
                  <ExecutionCapabilityNumber>{strength.number}</ExecutionCapabilityNumber>
                  <ItemTitle style={{ marginBottom: 10 }}>{strength.title}</ItemTitle>
                  <StrengthCardDescription>{strength.description}</StrengthCardDescription>
                </ExecutionCapabilityCard>
              ))}
            </ItemGrid>
          </Container>
        </Section>

        <SectionDivider />

        {/* Execution Framework Architecture */}
        <Section data-animate isVisible={isVisible('section-2')}>
          <Container>
            <SectionTitle className="with-underline">{home.ourServices.title}</SectionTitle>
            <BulletCardsGrid style={{ marginTop: '32px' }}>
              {(home.ourServices.items || []).map((item, index) => {
                const title = typeof item === 'string' ? item : item.title;
                const description = typeof item === 'object' && item.description;
                return (
                  <BulletCard key={index}>
                    <BulletCardIcon>
                      <CheckIcon />
                    </BulletCardIcon>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <StrengthCardTitle style={{ marginBottom: description ? '8px' : 0, fontSize: '18px' }}>{title}</StrengthCardTitle>
                      {description && <BulletCardText>{description}</BulletCardText>}
                    </div>
                  </BulletCard>
                );
              })}
            </BulletCardsGrid>
            <ToggleButtonContainer>
              <ToggleButton to={getLocalePath('/services')}>
                {home.ourServices.buttonText}
              </ToggleButton>
            </ToggleButtonContainer>
          </Container>
        </Section>

        <SectionDivider />

        {/* Industrial Execution Ecosystem */}
        <InstitutionalSection>
          <Container>
            <SectionTitle className="with-underline">{home.whoWeEngageWith.title}</SectionTitle>
            <InstitutionalBody>
              <p>{home.whoWeEngageWith.intro}</p>
            </InstitutionalBody>
            <WhoWeEngageGrid className="two-col">
              <div>
                <WhoWeEngageColumnLabel>{home.whoWeEngageWith.clientsSection.label}</WhoWeEngageColumnLabel>
                <BulletCardsGrid>
                  {home.whoWeEngageWith.clientsSection.items.map((item, index) => (
                    <CenteredBulletCard key={`who-we-engage-clients-${index}`}>
                      <BulletCardIcon>
                        <CheckIcon />
                      </BulletCardIcon>
                      <BulletCardText>{item}</BulletCardText>
                    </CenteredBulletCard>
                  ))}
                </BulletCardsGrid>
              </div>
              <div>
                <WhoWeEngageColumnLabel>{home.whoWeEngageWith.servicesSection.label}</WhoWeEngageColumnLabel>
                <BulletCardsGrid>
                  {home.whoWeEngageWith.servicesSection.items.map((item, index) => (
                    <CenteredBulletCard key={`who-we-engage-services-${index}`}>
                      <BulletCardIcon>
                        <CheckIcon />
                      </BulletCardIcon>
                      <BulletCardText>{item}</BulletCardText>
                    </CenteredBulletCard>
                  ))}
                </BulletCardsGrid>
              </div>
            </WhoWeEngageGrid>
          </Container>
        </InstitutionalSection>

        <SectionDivider />

        {/* Sector Expertise */}
        <Section data-animate isVisible={isVisible('section-3')}>
          <Container>
            <SectionTitle className="with-underline">{home.solutions.title}</SectionTitle>
            <ContentText>
              <p>{home.solutions.description}</p>
            </ContentText>
            <Grid className="two-col solutions-grid">
              {solutionsList.map((solution, index) => (
                <PreloadZone
                  key={solution.id}
                  preload={preloadSolutionsOnHover(solution.path)}
                  inline
                >
                  <SolutionCardLink
                    to={getLocalePath(solution.path)}
                    $hasImage={Boolean(solution.image)}
                    isVisible={isVisible('section-3')}
                    delay={`${0.05 + index * 0.05}s`}
                  >
                  {solution.image && (
                    <IndustryImage
                      $hasImage={Boolean(solution.image)}
                      $image={solution.image}
                    />
                  )}
                  <IndustryContent>
                    <Title mb="8px">{solution.title}</Title>
                    <Description size="14px">{solution.description}</Description>
                  </IndustryContent>
                  <CardArrowButton><CardArrowIcon /></CardArrowButton>
                </SolutionCardLink>
                </PreloadZone>
              ))}
            </Grid>
          </Container>
        </Section>

        <SectionDivider />

        {/* Why Apexgate Global */}
        <Section data-animate isVisible={isVisible('section-4')}>
          <Container>
            <SectionTitle className="with-underline">{home.whyApexgateGlobal.title}</SectionTitle>
            <ContentText>
              {(home.whyApexgateGlobal.paragraphs || [home.whyApexgateGlobal.paragraph]).filter(Boolean).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </ContentText>
            <ItemGrid style={{ marginTop: 32 }}>
              {home.whyApexgateGlobal.items.map((item, index) => (
                <AnimatedItemCard
                  key={index}
                  $isVisible={isVisible('section-4')}
                  $delay={`${0.05 + index * 0.08}s`}
                >
                  <ItemTitle style={{ marginBottom: 10 }}>{item.title}</ItemTitle>
                  <StrengthCardDescription>{item.description}</StrengthCardDescription>
                </AnimatedItemCard>
              ))}
            </ItemGrid>
          </Container>
        </Section>

        <SectionDivider />

        {/* Partnership CTA — same pattern as About / solutions (CtaImageSection + compact type + glass button) */}
        <Section data-animate isVisible={isVisible('section-5')}>
          <Container>
            <CtaImageSection src="/ui/handshake-loop.webp" overlay={0.45} critical>
              <PartnershipSection
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
              >
                <PartnershipContent>
                  <SolutionsCtaTitle as={motion.h3} variants={staggerItem}>
                    {home.partnership.title}
                  </SolutionsCtaTitle>
                  <SolutionsCtaText as={motion.p} variants={staggerItem}>
                    {home.partnership.paragraphs.map((paragraph, index) => (
                      <React.Fragment key={index}>
                        {paragraph}
                        {index < home.partnership.paragraphs.length - 1 && <><br /><br /></>}
                      </React.Fragment>
                    ))}
                  </SolutionsCtaText>
                  <SolutionsCtaButton to={home.partnership.buttonLink}>
                    {home.partnership.buttonText}
                  </SolutionsCtaButton>
                </PartnershipContent>
              </PartnershipSection>
            </CtaImageSection>
          </Container>
        </Section>
      </HomeContainer>
    </>
  );
};

export default Home;
